/*!
 * AGC Tracking Capture v1.0
 * -------------------------
 * 1. Reads click IDs + UTM params from URL, persists to 1st-party cookies (90d).
 * 2. Hydrates window.agcTracking with the captured values.
 * 3. On first load (and on navigation to a new context), pushes them to
 *    Shopify cart attributes via /cart/update.js so any order created from
 *    this session (incl. admin draft orders) carries the context in
 *    order.note_attributes.
 * 4. Rewrites every WhatsApp anchor on the page (theme header .hdt-social-whatsapp,
 *    app-embed widget, any [href*="whatsapp.com/send"] or [href*="wa.me"]) to
 *    include a pre-filled message with page context + click IDs.
 * 5. Re-runs the WA-rewrite on DOM mutations so floating widgets injected
 *    late (app-embed iframe siblings, etc.) are still upgraded.
 *
 * No dependencies. IE11-safe avoidance not needed — Shopify Dawn/Hydrogen era.
 */
(function () {
  "use strict";

  var CTX = window.AGC_CTX || {};
  var KEYS = CTX.trackedKeys || [];
  var COOKIE_DAYS = CTX.cookieDays || 90;
  var COOKIE_DOMAIN = CTX.cookieDomain || "";
  var WA_PHONE = (CTX.waPhone || "").replace(/\D/g, "");
  // Feature flag (default ON). Kill-switch via `AGC_CTX.enableAcquisitionChannel = false`
  // in the Liquid snippet — disables only the 14-bucket classifier; legacy
  // `classifyReferrer()` + cookies continue to flow untouched.
  var ENABLE_ACQ_CHANNEL = CTX.enableAcquisitionChannel !== false;

  // ---------- acquisition_channel: 14-bucket closed set ----------
  // Per spec `MARKETING/exports/agc-source-channel-taxonomy.md` §1.
  // Byte-identical to Coway v1.0. Do NOT split/rename without cross-brand re-spec.
  var VALID_ACQUISITION_CHANNELS = {
    paid_google: 1, paid_meta: 1, paid_other: 1,
    llm_chatgpt: 1, llm_perplexity: 1, llm_gemini: 1, llm_copilot: 1, llm_claude: 1, llm_other: 1,
    organic_search: 1, social_meta: 1, social_other: 1,
    referral: 1, direct: 1
  };

  var PAID_GOOGLE_PARAMS = ["gclid", "gbraid", "wbraid"];
  var PAID_MEDIUM_RE = /^(cpc|ppc|paid|paidsearch|paidsocial|display)$/i;
  var PAID_SOURCE_GOOGLE_RE = /^(google|youtube)$/i;
  var PAID_SOURCE_META_RE = /^(facebook|instagram|meta|fb|ig)$/i;

  var LLM_HOSTS = [
    ["llm_chatgpt",    /^(www\.)?(chatgpt\.com|chat\.openai\.com|openai\.com)$/i],
    ["llm_perplexity", /^(www\.)?(perplexity\.ai|pplx\.ai)$/i],
    ["llm_gemini",     /^(www\.)?(gemini\.google\.com|bard\.google\.com)$/i],
    ["llm_claude",     /^(www\.)?claude\.ai$/i],
    ["llm_other",      /^(www\.)?(x\.ai|grok\.com|you\.com|phind\.com|poe\.com|chat\.mistral\.ai|deepseek\.com|chat\.deepseek\.com|qwenlm\.ai|tongyi\.aliyun\.com|kimi\.moonshot\.cn|chat\.z\.ai|meta\.ai)$/i]
  ];

  var SEARCH_HOSTS_RE = /^(www\.)?(google\.[a-z.]{2,8}|bing\.com|duckduckgo\.com|search\.yahoo\.com|yahoo\.com|yandex\.(com|ru)|baidu\.com|naver\.com|ecosia\.org|brave\.com|search\.brave\.com|startpage\.com|qwant\.com)$/i;
  var SOCIAL_META_RE = /^(www\.|m\.|l\.|lm\.|business\.)?(facebook\.com|instagram\.com|messenger\.com|fb\.com|fb\.me|fb\.watch|instagr\.am)$/i;
  var SOCIAL_OTHER_RE = /^(www\.|m\.|mobile\.)?(tiktok\.com|vm\.tiktok\.com|linkedin\.com|lnkd\.in|twitter\.com|x\.com|t\.co|youtube\.com|youtu\.be|snapchat\.com|reddit\.com|redd\.it|pinterest\.com|pin\.it|threads\.net|threads\.com|telegram\.org|t\.me|weibo\.com|quora\.com|medium\.com)$/i;

  // AGC internal-nav allowlist — spec §2 + §9.3. Strip www. before compare.
  var AGC_INTERNAL_HOSTS = { "acaciagardencenter.com": 1, "244768-3.myshopify.com": 1 };

  function isCopilot(refHost, refPath) {
    if (/^(www\.)?copilot\.microsoft\.com$/i.test(refHost)) return true;
    if (/^(www\.)?edgeservices\.bing\.com$/i.test(refHost)) return true;
    if (/^(www\.)?bing\.com$/i.test(refHost) && /^\/(chat|copilot)(\/|\?|$)/i.test(refPath || "")) return true;
    return false;
  }

  /**
   * Classify the acquisition channel (14-bucket closed set) — spec §3.
   * @param {URLSearchParams|null} params — URL query params (null = no params)
   * @param {URL|null} referrerUrl — parsed document.referrer (null = no referrer)
   * @param {string} currentHost — location.hostname
   * @returns {string|null} — bucket name, or null to signal internal-nav (skip write)
   */
  function classifyAcquisitionChannel(params, referrerUrl, currentHost) {
    // Step 1-3: paid click IDs / paid utm_medium win unconditionally.
    if (params) {
      for (var i = 0; i < PAID_GOOGLE_PARAMS.length; i++) {
        if (params.get(PAID_GOOGLE_PARAMS[i])) return "paid_google";
      }
      if (params.get("fbclid")) return "paid_meta";
      var medium = (params.get("utm_medium") || "").trim();
      if (PAID_MEDIUM_RE.test(medium)) {
        var source = (params.get("utm_source") || "").trim();
        if (PAID_SOURCE_GOOGLE_RE.test(source)) return "paid_google";
        if (PAID_SOURCE_META_RE.test(source))   return "paid_meta";
        return "paid_other";
      }
    }

    // Step 8: no referrer → direct.
    if (!referrerUrl) return "direct";
    var refHost = (referrerUrl.hostname || "").toLowerCase();
    var refPath = referrerUrl.pathname || "";

    // Step 9: internal nav → skip (do NOT overwrite first-touch cookie).
    var normCurrent = (currentHost || "").toLowerCase().replace(/^www\./, "");
    var normRef = refHost.replace(/^www\./, "");
    if (normRef && (normRef === normCurrent || AGC_INTERNAL_HOSTS[normRef])) return null;

    // Step 4a: Copilot path-check FIRST (covers bing.com/chat).
    if (isCopilot(refHost, refPath)) return "llm_copilot";
    // Step 4b: other LLM hosts.
    for (var j = 0; j < LLM_HOSTS.length; j++) {
      if (LLM_HOSTS[j][1].test(refHost)) return LLM_HOSTS[j][0];
    }
    // Step 5: search engines (excl. LLM paths, handled above).
    if (SEARCH_HOSTS_RE.test(refHost)) return "organic_search";
    // Step 6: social.
    if (SOCIAL_META_RE.test(refHost))  return "social_meta";
    if (SOCIAL_OTHER_RE.test(refHost)) return "social_other";
    // Step 7: unknown referrer → referral.
    return "referral";
  }

  // Expose for testing (harness reads this).
  window.__agcClassifyAcquisitionChannel = classifyAcquisitionChannel;

  // ---------- cookie helpers ----------
  function setCookie(name, value, days) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + days * 864e5);
      var parts = [
        "agc_" + name + "=" + encodeURIComponent(value),
        "expires=" + d.toUTCString(),
        "path=/",
        "SameSite=Lax"
      ];
      if (location.protocol === "https:") parts.push("Secure");
      if (COOKIE_DOMAIN) parts.push("domain=" + COOKIE_DOMAIN);
      document.cookie = parts.join("; ");
    } catch (e) { /* noop */ }
  }
  function getCookie(name) {
    var match = document.cookie.match(new RegExp("(?:^|; )agc_" + name + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : "";
  }

  // ---------- capture from URL -> cookie, then rehydrate ----------
  function captureAndHydrate() {
    var params;
    try { params = new URLSearchParams(location.search); } catch (e) { params = null; }
    var tracking = {};
    KEYS.forEach(function (k) {
      var urlVal = params ? params.get(k) : null;
      if (urlVal) {
        setCookie(k, urlVal, COOKIE_DAYS);
        tracking[k] = urlVal;
      } else {
        var cookieVal = getCookie(k);
        if (cookieVal) tracking[k] = cookieVal;
      }
    });
    // Referrer classification — first-touch only, and only when no paid click ID is present.
    // Paid click IDs (captured above) always win; if any present, we do NOT overwrite an
    // existing agc_traffic_source/agc_source_detail cookie. Classifier writes once and
    // uses the same setCookie helper (inherits the standard TTL/SameSite/Secure behavior).
    var hasPaidClickId = !!(tracking.gclid || tracking.fbclid || tracking.msclkid ||
                            tracking.ctwa_clid || tracking.gbraid || tracking.wbraid);
    var existingTs = getCookie("traffic_source");
    if (!existingTs && !hasPaidClickId) {
      var cls = classifyReferrer();
      if (cls) { // null = same-origin, skip
        setCookie("traffic_source", cls.traffic_source, COOKIE_DAYS);
        setCookie("source_detail", cls.source_detail, COOKIE_DAYS);
        tracking.traffic_source = cls.traffic_source;
        tracking.source_detail = cls.source_detail;
      }
    } else {
      if (existingTs) tracking.traffic_source = existingTs;
      var existingSd = getCookie("source_detail");
      if (existingSd) tracking.source_detail = existingSd;
    }

    // Stamp landing_page + first_touch_at on first capture; refresh last_touch_at each load
    var nowIso = new Date().toISOString();
    if (!getCookie("landing_page")) {
      setCookie("landing_page", location.href, COOKIE_DAYS);
    }
    if (!getCookie("first_touch_at")) {
      setCookie("first_touch_at", nowIso, COOKIE_DAYS);
    }
    setCookie("last_touch_at", nowIso, COOKIE_DAYS);

    tracking.landing_page = getCookie("landing_page") || location.href;
    tracking.first_touch_at = getCookie("first_touch_at") || nowIso;
    tracking.last_touch_at = nowIso;

    // Derive source_channel from available click IDs (priority: ctwa > gclid > fbclid > msclkid > utm > direct)
    tracking.source_channel = tracking.ctwa_clid ? "meta_ctwa"
                             : tracking.gclid    ? "google_ads"
                             : tracking.fbclid   ? "meta_organic_or_ad"
                             : tracking.msclkid  ? "bing_ads"
                             : tracking.utm_source ? ("utm_" + tracking.utm_source)
                             : "direct_or_other";

    // ---- acquisition_channel (14-bucket) — first-touch, additive to legacy classifyReferrer() ----
    // Independent of the paid-click-ID gate above: a returning visitor with gclid
    // still gets their *first-touch* acquisition preserved. Feature-flagged via
    // AGC_CTX.enableAcquisitionChannel (default ON).
    if (ENABLE_ACQ_CHANNEL) {
      var existingAcq = getCookie("acquisition_channel");
      if (existingAcq) {
        tracking.acquisition_channel = existingAcq;
      } else {
        var acqParams = null;
        try { acqParams = new URLSearchParams(location.search); } catch (e) { acqParams = null; }
        var refUrl = null;
        try { refUrl = document.referrer ? new URL(document.referrer) : null; } catch (e) { refUrl = null; }
        var bucket = classifyAcquisitionChannel(acqParams, refUrl, location.hostname);
        // null = internal nav → skip. Defensive: closed-set validation on emit.
        if (bucket && VALID_ACQUISITION_CHANNELS[bucket]) {
          setCookie("acquisition_channel", bucket, COOKIE_DAYS);
          tracking.acquisition_channel = bucket;
        }
      }
    }

    window.agcTracking = tracking;
    return tracking;
  }

  // ---------- push to Shopify cart attributes ----------
  //
  // DUAL-WRITE: Each canonical attribution key is written to cart.attributes
  // TWICE — once with underscore prefix (e.g. "_gclid") which hides it from
  // the checkout UI per Shopify convention, and once UNPREFIXED (e.g. "gclid")
  // so downstream consumers can read it from order.note_attributes without
  // guessing about the underscore.
  //
  // Shopify promotes cart.attributes to order.note_attributes as-is (keys kept
  // verbatim, underscore and all). The offline conversion upload scripts
  // (Tracking Diagnostics) and the Digital Sales Closer manual fallback SOP
  // both read the UNPREFIXED keys as the source of truth. The prefixed keys
  // remain so the checkout UI stays clean for customers.
  // ----------------------------------------------------------------------
  var cartSyncedHash = "";

  // Canonical set of keys to dual-write. Order doesn't matter; used only for
  // the sync hash + the /cart/update.js payload.
  var CANONICAL_KEYS = [
    "gclid", "gbraid", "wbraid", "fbclid", "ctwa_clid", "msclkid",
    "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
    "source_channel", "landing_page", "first_touch_at", "last_touch_at",
    "traffic_source", "source_detail", "acquisition_channel"
  ];

  function syncCartAttributes(tracking) {
    // Build attribute map. Each value gets written BOTH prefixed (hidden from
    // checkout UI) AND unprefixed (readable by offline-upload scripts &
    // manual-fallback SOP from order.note_attributes).
    var attrs = {};
    CANONICAL_KEYS.forEach(function (k) {
      var v = tracking[k];
      if (v === undefined || v === null || v === "") return;
      attrs["_" + k] = v;   // hidden-in-UI variant
      attrs[k]       = v;   // script-readable variant
    });

    // Dedup: only call /cart/update.js if attrs changed this session.
    // Exclude last_touch_at from the hash so we don't POST on every pageload.
    var hashAttrs = {};
    Object.keys(attrs).forEach(function (k) {
      if (k === "last_touch_at" || k === "_last_touch_at") return;
      hashAttrs[k] = attrs[k];
    });
    var hash = JSON.stringify(hashAttrs);
    if (hash === cartSyncedHash) return;
    cartSyncedHash = hash;

    try {
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ attributes: attrs }),
        credentials: "same-origin"
      }).catch(function () { /* swallow */ });
    } catch (e) { /* noop */ }
  }

  // ---------- WhatsApp message builder (shared by anchor rewriter + button interceptor) ----------
  //
  // WA-preview UX note: WhatsApp desktop/web shows the first few lines of the
  // pre-filled text in the chat input BEFORE the user hits send. A visible
  // `[ref: gclid=...; fbclid=...]` block looks like spam/phishing to customers.
  // We bury it ABOVE ten period-only lines ("." lines, NOT blank lines —
  // WhatsApp collapses consecutive blank lines in the preview pane but
  // tolerates period-only lines). WA cursor lands at end → user sees greeting,
  // ref hidden above — so we put the ref block FIRST and the greeting LAST.
  //
  // Frozen `text=` contract (see docs/DEPLOYMENT.md §4.4):
  //   Line 1:   [ref: k1=v1; k2=v2; ...]   (omitted if refBits empty)
  //   Lines 2-11: "." (single period per line, x10)
  //   Line 12:  Hi, I'm interested in {product}   OR   Hi, I need help on the website
  //   Line 13:  {pageUrl}
  // Gallabox Worker regex /\[ref:\s*([^\]]+)\]/ works unchanged — [^\]] matches \n.
  var REF_KEYS = [
    "gclid","gbraid","wbraid","fbclid","ctwa_clid",
    "utm_source","utm_medium","utm_campaign","utm_content","utm_term",
    "traffic_source","source_detail","acquisition_channel"
  ];

  // ---------- referrer classifier (LLM / organic_search / social / referral / direct) ----------
  // First-touch only: writes agc_traffic_source + agc_source_detail cookies exactly once.
  // Paid click IDs (gclid/fbclid/msclkid/ctwa_clid/gbraid/wbraid) win — caller skips this fn.
  // Hostname-only matching; ignores path/query. Safe to throw → defaults to direct.
  function classifyReferrer() {
    var out = { traffic_source: "direct", source_detail: "" };
    try {
      var ref = document.referrer || "";
      if (!ref) return out;
      var u; try { u = new URL(ref); } catch (e) { return out; }
      var h = (u.hostname || "").toLowerCase().replace(/^www\./, "");
      if (!h || h === location.hostname.toLowerCase().replace(/^www\./, "")) return null; // same-origin → skip
      var path = (u.pathname || "") + (u.search || "");
      // LLM (highest priority)
      if (h === "chat.openai.com" || h === "chatgpt.com") return { traffic_source: "llm", source_detail: "chatgpt" };
      if (h === "claude.ai" || h === "anthropic.com" || h.endsWith(".anthropic.com")) return { traffic_source: "llm", source_detail: "claude" };
      if (h === "perplexity.ai" || h.endsWith(".perplexity.ai")) return { traffic_source: "llm", source_detail: "perplexity" };
      if (h === "gemini.google.com" || h === "bard.google.com") return { traffic_source: "llm", source_detail: "gemini" };
      if (h === "copilot.microsoft.com" || (h === "bing.com" && path.indexOf("/chat") === 0)) return { traffic_source: "llm", source_detail: "copilot" };
      if (h === "you.com") return { traffic_source: "llm", source_detail: "you_com" };
      if (h === "phind.com") return { traffic_source: "llm", source_detail: "phind" };
      if (h === "poe.com") return { traffic_source: "llm", source_detail: "poe" };
      if (h === "meta.ai") return { traffic_source: "llm", source_detail: "meta_ai" };
      if (h === "duckduckgo.com" && path.indexOf("ia=chat") !== -1) return { traffic_source: "llm", source_detail: "ddg_ai" };
      // Social
      if (h === "instagram.com" || h === "l.instagram.com") return { traffic_source: "social", source_detail: "instagram" };
      if (h === "facebook.com" || h === "l.facebook.com" || h === "m.facebook.com" || h === "lm.facebook.com") return { traffic_source: "social", source_detail: "facebook" };
      if (h === "tiktok.com" || h === "vm.tiktok.com" || h.endsWith(".tiktok.com")) return { traffic_source: "social", source_detail: "tiktok" };
      if (h === "linkedin.com" || h === "lnkd.in" || h.endsWith(".linkedin.com")) return { traffic_source: "social", source_detail: "linkedin" };
      if (h === "reddit.com" || h.endsWith(".reddit.com")) return { traffic_source: "social", source_detail: "reddit" };
      if (h === "x.com" || h === "twitter.com" || h === "t.co" || h.endsWith(".twitter.com")) return { traffic_source: "social", source_detail: "x" };
      if (h === "youtube.com" || h === "youtu.be" || h === "m.youtube.com") return { traffic_source: "social", source_detail: "youtube" };
      if (h === "pinterest.com" || h === "pin.it" || h.endsWith(".pinterest.com")) return { traffic_source: "social", source_detail: "pinterest" };
      if (h === "snapchat.com" || h.endsWith(".snapchat.com")) return { traffic_source: "social", source_detail: "snapchat" };
      // Organic search (google.* covers all TLDs; gemini/bard handled above)
      if (h === "google.com" || h.indexOf("google.") === 0 || h.indexOf(".google.") !== -1) return { traffic_source: "organic_search", source_detail: "google" };
      if (h === "bing.com") return { traffic_source: "organic_search", source_detail: "bing" };
      if (h === "duckduckgo.com") return { traffic_source: "organic_search", source_detail: "ddg" };
      if (h === "yandex.com" || h === "yandex.ru") return { traffic_source: "organic_search", source_detail: "yandex" };
      if (h === "baidu.com") return { traffic_source: "organic_search", source_detail: "baidu" };
      if (h === "yahoo.com" || h.endsWith(".yahoo.com")) return { traffic_source: "organic_search", source_detail: "yahoo" };
      if (h === "ecosia.org") return { traffic_source: "organic_search", source_detail: "ecosia" };
      if (h === "search.brave.com") return { traffic_source: "organic_search", source_detail: "brave" };
      // Any other external domain
      return { traffic_source: "referral", source_detail: h };
    } catch (e) { return out; }
  }

  function buildWaMessage(tracking, pageUrl, productName) {
    tracking = tracking || {};
    var greeting = productName
      ? "Hi, I'm interested in " + productName
      : "Hi, I need help on the website";

    var refBits = [];
    REF_KEYS.forEach(function (k) {
      var v = tracking[k];
      if (!v) return;
      // Defensive closed-set validation on emit: if acquisition_channel
      // somehow holds a value outside the 14-bucket set (shouldn't happen,
      // but cookie contents are attacker-influenced), omit the key rather
      // than leak garbage into the [ref:] block / worker / metafield.
      if (k === "acquisition_channel" && !VALID_ACQUISITION_CHANNELS[v]) return;
      refBits.push(k + "=" + v);
    });

    // Ref goes on TOP: WA cursor lands at end → user sees greeting, ref hidden above.
    var lines = [];
    if (refBits.length) lines.push("[ref: " + refBits.join("; ") + "]");
    // Ten period-only filler lines — pushes the [ref:] block above WA's
    // preview fold on desktop while keeping it parseable server-side.
    for (var i = 0; i < 10; i++) lines.push(".");
    lines.push(greeting);
    lines.push(pageUrl || "");
    return lines.join("\n");
  }

  function rewriteWaLinks(tracking) {
    if (!WA_PHONE) return;
    var msg = buildWaMessage(
      tracking,
      CTX.pageUrl || location.href,
      CTX.productName || ""
    );
    var encoded = encodeURIComponent(msg);
    var target = "https://wa.me/" + WA_PHONE + "?text=" + encoded;

    // Match any WA anchor: header social link, floating app-embed widget,
    // PDP CTA, footer link, etc.
    var selectors = [
      'a.hdt-social-whatsapp',
      'a[href*="wa.me/"]',
      'a[href*="whatsapp.com/send"]',
      'a[href*="api.whatsapp.com/send"]'
    ];
    var nodes = document.querySelectorAll(selectors.join(","));
    nodes.forEach(function (a) {
      // Only rewrite if the href points at our number (or no number filter)
      var href = a.getAttribute("href") || "";
      var hrefDigits = (href.match(/\d{8,}/) || [""])[0];
      if (!hrefDigits || hrefDigits.indexOf(WA_PHONE) !== -1) {
        a.setAttribute("href", target);
        a.setAttribute("data-agc-wa-upgraded", "1");
        // Ensure the click opens the native app / web WA correctly
        if (!a.getAttribute("target")) a.setAttribute("target", "_blank");
        if (!a.getAttribute("rel")) a.setAttribute("rel", "noopener");
      }
    });
  }

  // ---------- click handler: fire cart sync on WA click ----------
  function onAnyClick(e) {
    var a = e.target && e.target.closest ? e.target.closest('a[data-agc-wa-upgraded="1"]') : null;
    if (!a) return;
    // Last-chance sync before the user leaves for WA
    syncCartAttributes(window.agcTracking || {});
  }

  // ---------- non-anchor WA widget interceptor (capture phase) ----------
  // Intercept non-anchor WA widgets (e.g., <button id="chwhatsapp-btn"> from a
  // Shopify app) that open WA via JS handler rather than anchor href.
  var NON_ANCHOR_SELECTORS = [
    '#chwhatsapp-btn',
    'button.whatsappbutton',
    '[data-whatsapp-trigger]'
  ];

  function onNonAnchorWaClick(e) {
    var target = e.target && e.target.closest ? e.target.closest(NON_ANCHOR_SELECTORS.join(',')) : null;
    if (!target) return;
    if (target.tagName === 'A') return; // anchors already handled by rewriter
    if (target.dataset.agcWaIntercepted === '1') {
      // already processed on a prior click — don't double-fire
      return;
    }

    // Stop the app's own click handler from running
    e.stopPropagation();
    e.preventDefault();
    target.dataset.agcWaIntercepted = '1';

    // Build the wa.me URL with tracking context (same format as anchor rewrite)
    var phone = WA_PHONE || '971525725254';
    var tracking = window.agcTracking || {};
    // Only use real product title (PDP). Don't fall back to og:title or
    // document.title — those pick up the homepage/collection title and break
    // the "Hi, I need help on the website" generic greeting on non-PDP pages.
    var productName = CTX.productName || '';
    var pageUrl = CTX.pageUrl || location.href;
    var message = buildWaMessage(tracking, pageUrl, productName);

    // Fire last-chance cart sync BEFORE leaving
    if (typeof syncCartAttributes === 'function' && tracking) {
      try { syncCartAttributes(tracking); } catch (err) { /* noop */ }
    }

    var waUrl = 'https://wa.me/' + phone + '?text=' + encodeURIComponent(message);
    window.open(waUrl, '_blank', 'noopener');
    // Reset the flag after a tick so subsequent clicks work too
    setTimeout(function () { target.dataset.agcWaIntercepted = ''; }, 500);
  }

  // ---------- boot ----------
  function boot() {
    var tracking = captureAndHydrate();
    syncCartAttributes(tracking);
    rewriteWaLinks(tracking);

    // Observe DOM for late-injected WA widgets (app-embed iframes inject siblings)
    try {
      var mo = new MutationObserver(function () {
        rewriteWaLinks(window.agcTracking || tracking);
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) { /* noop */ }

    document.addEventListener("click", onAnyClick, true);
    // capture phase = runs before app's own handler
    document.addEventListener("click", onNonAnchorWaClick, true);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
